import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Loader2, AlertCircle, CheckCircle2, Lock } from 'lucide-react';

export default function ForcedPasswordResetPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const passwordValid = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid || !passwordsMatch) return;
    setError(null);
    setLoading(true);
    try {
      await authAPI.resetPassword({ new_password: newPassword, confirm_password: confirmPassword });
      setSuccess(true);
      await refreshUser();
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center px-4" style={{ background: 'hsl(var(--muted))' }}>
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-1">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
            style={{ background: 'hsl(var(--accent))' }}
          >
            <Lock size={20} style={{ color: 'hsl(var(--c4p-navy-900))' }} />
          </div>
          <CardTitle style={{ fontFamily: 'var(--font-display)' }}>Set your password</CardTitle>
          <CardDescription>
            For security, please set a new password before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'hsl(152 55% 34% / 0.1)' }}>
              <CheckCircle2 size={20} style={{ color: 'hsl(var(--success))' }} />
              <p className="text-sm font-medium">Password set successfully. Redirecting...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  data-testid="reset-new-password-input"
                />
                {newPassword && !passwordValid && (
                  <p className="text-xs" style={{ color: 'hsl(var(--danger))' }}>Minimum 8 characters</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="reset-confirm-password-input"
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs" style={{ color: 'hsl(var(--danger))' }}>Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !passwordValid || !passwordsMatch}
                style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}
                data-testid="reset-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set password & continue'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
